// Image service for handling local development vs production URLs
export interface GalleryImage {
  id: string;
  title: string;
  description: string;
  category: string;
  ratio: string;
  portfolioLink: string;
  filename: string; // Full-size filename
  thumbnailFilename?: string; // Optional different thumbnail filename
}

class ImageService {
  private isDevelopment = import.meta.env.DEV;
  private cloudfrontDomain = import.meta.env.VITE_CLOUDFRONT_DOMAIN || '';
  
  getImageUrl(category: string, filename: string, isThumb = false): string {
    const folder = isThumb ? 'thumbs' : 'full'
    
    if (this.isDevelopment) {
      const thumbPath = isThumb ? '/thumbs' : ''
      return `/images/portfolio/${category}${thumbPath}/${filename}`;
    } else {
      return `https://${this.cloudfrontDomain}/images/portfolio/${category}/${folder}/${filename}`;
    }
  }
  
  getThumbnailUrl(image: GalleryImage): string {
    // If no thumbnailFilename specified, fall back to full-size image
    if (!image.thumbnailFilename) {
      return this.getFullSizeUrl(image);
    }
    
    // Use the specified thumbnail filename
    if (this.isDevelopment) {
      return `/images/portfolio/${image.category}/thumbs/${image.thumbnailFilename}`;
    } else {
      return `https://${this.cloudfrontDomain}/images/portfolio/${image.category}/thumbs/${image.thumbnailFilename}`;
    }
  }
  
  // Helper method to check if thumbnail likely exists
  hasThumbnail(image: GalleryImage): boolean {
    return !!image.thumbnailFilename;
  }
  
  getFullSizeUrl(image: GalleryImage): string {
    if (this.isDevelopment) {
      return `/images/portfolio/${image.category}/full/${image.filename}`;
    } else {
      return `https://${this.cloudfrontDomain}/images/portfolio/${image.category}/full/${image.filename}`;
    }
  }
  
  // Gallery images with actual dimensions and calculated ratios
  getGalleryImages(): GalleryImage[] {
    return [
      // Bridal
      { 
        id: "bridal-1", 
        title: "Elegant Bridal Portrait", 
        description: "Timeless bridal photography capturing pure elegance", 
        category: "bridal",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/bridal", 
        filename: "bridal-portrait-1.jpg" 
      },
      { 
        id: "bridal-2", 
        title: "Bridal Beauty", 
        description: "Delicate bridal makeup and styling", 
        category: "bridal",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/bridal", 
        filename: "bridal-portrait-2.jpg" 
      },
      
      // Fashion
      { 
        id: "fashion-1", 
        title: "Fashion Editorial", 
        description: "High-fashion editorial photography", 
        category: "fashion",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/fashion", 
        filename: "fashion-editorial-1.jpg" 
      },
      { 
        id: "fashion-2", 
        title: "Fashion Portrait", 
        description: "Contemporary fashion photography", 
        category: "fashion",
        ratio: "4000/2649", 
        portfolioLink: "/portfolio/fashion", 
        filename: "fashion-editorial-2.jpg" 
      },
      
      // Seniors
      { 
        id: "senior-1", 
        title: "Senior Portrait", 
        description: "Creative senior photography with personality", 
        category: "seniors",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/seniors", 
        filename: "senior-portrait-1.jpg" 
      },
      
      // Modeling (11 images)
      { 
        id: "model-1", 
        title: "Model Portfolio", 
        description: "Professional modeling portfolio development", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-1.jpg" 
      },
      { 
        id: "model-2", 
        title: "Fashion Model", 
        description: "Professional model headshot session", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-2.jpg" 
      },
      { 
        id: "model-3", 
        title: "Commercial Model", 
        description: "Commercial modeling photography", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-3.jpg" 
      },
      { 
        id: "model-4", 
        title: "Editorial Model", 
        description: "Editorial style modeling session", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-4.jpg" 
      },
      { 
        id: "model-5", 
        title: "Beauty Model", 
        description: "Beauty and cosmetic modeling", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-5.jpg" 
      },
      { 
        id: "model-6", 
        title: "Lifestyle Model", 
        description: "Lifestyle modeling photography", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-6.jpg" 
      },
      { 
        id: "model-7", 
        title: "Portrait Model", 
        description: "Professional portrait modeling", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-7.jpg" 
      },
      { 
        id: "model-8", 
        title: "Studio Model", 
        description: "Studio modeling session", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-8.jpg" 
      },
      { 
        id: "model-9", 
        title: "Creative Model", 
        description: "Creative modeling concepts", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-9.jpg" 
      },
      { 
        id: "model-10", 
        title: "Fashion Model", 
        description: "High-end fashion modeling", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-10.jpg" 
      },
      { 
        id: "model-11", 
        title: "Professional Model", 
        description: "Professional modeling portfolio", 
        category: "modeling",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/modeling", 
        filename: "model-portfolio-11.jpg" 
      },
      
      // Creative (3 images)
      { 
        id: "creative-1", 
        title: "Creative Concept", 
        description: "Artistic interpretation with unique styling", 
        category: "creative",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/creative", 
        filename: "creative-concept-1.jpg" 
      },
      { 
        id: "creative-2", 
        title: "Artistic Vision", 
        description: "Conceptual photography with artistic flair", 
        category: "creative",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/creative", 
        filename: "creative-concept-2.jpg" 
      },
      { 
        id: "creative-3", 
        title: "Creative Portrait", 
        description: "Innovative creative photography concepts", 
        category: "creative",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/creative", 
        filename: "creative-concept-3.jpg" 
      },
      
      // Headshots (5 images)
      { 
        id: "headshot-1", 
        title: "Professional Headshot", 
        description: "Corporate and creative headshot photography", 
        category: "headshots",
        ratio: "4000/2649", 
        portfolioLink: "/portfolio/headshots", 
        filename: "headshot-professional-1.jpg" 
      },
      { 
        id: "headshot-2", 
        title: "Business Portrait", 
        description: "Professional business headshot session", 
        category: "headshots",
        ratio: "3200/4000", 
        portfolioLink: "/portfolio/headshots", 
        filename: "headshot-professional-2.jpg" 
      },
      { 
        id: "headshot-3", 
        title: "Corporate Headshot", 
        description: "Executive corporate photography", 
        category: "headshots",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/headshots", 
        filename: "headshot-professional-3.jpg" 
      },
      { 
        id: "headshot-4", 
        title: "Professional Portrait", 
        description: "Clean professional headshot", 
        category: "headshots",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/headshots", 
        filename: "headshot-professional-4.jpg" 
      },
      { 
        id: "headshot-5", 
        title: "Executive Portrait", 
        description: "Executive level professional photography", 
        category: "headshots",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/headshots", 
        filename: "headshot-professional-5.jpg" 
      },
      
      // Graduation (3 images) - Note: disabled in settings
      { 
        id: "graduation-1", 
        title: "Graduation Portrait", 
        description: "Celebrating academic achievements", 
        category: "graduation",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/graduation", 
        filename: "graduation-1.jpg" 
      },
      { 
        id: "graduation-2", 
        title: "Graduate Session", 
        description: "Professional graduation photography", 
        category: "graduation",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/graduation", 
        filename: "graduation-2.jpg" 
      },
      { 
        id: "graduation-3", 
        title: "Commencement Photos", 
        description: "Milestone graduation photography", 
        category: "graduation",
        ratio: "2649/4000", 
        portfolioLink: "/portfolio/graduation", 
        filename: "graduation-3.jpg" 
      }
    ];
  }
}

export const imageService = new ImageService();
